/**
 * LUKHAS Screensaver - Activates after 2 minutes of inactivity
 *
 * "Simplicity is the ultimate sophistication." - Leonardo da Vinci
 */

import { useEffect, useState, useCallback } from 'react'
import { cn } from '../lib/utils'

const INACTIVITY_TIMEOUT = 2 * 60 * 1000 // 2 minutes

const CONSTELLATION_STARS = [
  { name: 'Identity', symbol: '⚛️', delay: 0 },
  { name: 'Memory', symbol: '✦', delay: 0.5 },
  { name: 'Vision', symbol: '🔬', delay: 1 },
  { name: 'Bio', symbol: '🌱', delay: 1.5 },
  { name: 'Dream', symbol: '🌙', delay: 2 },
  { name: 'Ethics', symbol: '⚖️', delay: 2.5 },
  { name: 'Guardian', symbol: '🛡️', delay: 3 },
  { name: 'Quantum', symbol: '⚛️', delay: 3.5 },
]

export default function Screensaver() {
  const [isActive, setIsActive] = useState(false)
  const [time, setTime] = useState(new Date())

  const resetTimer = useCallback(() => {
    setIsActive(false)
  }, [])

  useEffect(() => {
    let inactivityTimer: NodeJS.Timeout

    const startTimer = () => {
      clearTimeout(inactivityTimer)
      inactivityTimer = setTimeout(() => {
        setIsActive(true)
      }, INACTIVITY_TIMEOUT)
    }

    const handleActivity = () => {
      if (isActive) {
        resetTimer()
      }
      startTimer()
    }

    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll']
    events.forEach(event => {
      document.addEventListener(event, handleActivity)
    })

    // Start initial timer
    startTimer()

    return () => {
      clearTimeout(inactivityTimer)
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
    }
  }, [isActive, resetTimer])

  // Update clock
  useEffect(() => {
    if (!isActive) return
    const interval = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(interval)
  }, [isActive])

  if (!isActive) return null

  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#0a0a0f] flex flex-col items-center justify-center cursor-pointer"
      onClick={resetTimer}
    >
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-lambda-gold/5 via-transparent to-purple-500/5" />

      {/* Time display */}
      <div className="relative z-10 text-center mb-16">
        <p className="text-8xl font-extralight text-white tracking-wider">
          {time.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          })}
        </p>
        <p className="text-xl text-gray-500 mt-4 font-light">
          {time.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          })}
        </p>
      </div>

      {/* LUKHAS Logo */}
      <div className="relative z-10 mb-16">
        <h1 className="text-5xl font-light tracking-[0.3em] text-white/80">
          LUKH<span className="text-lambda-gold">Λ</span>S
        </h1>
        <p className="text-center text-gray-600 text-sm mt-2 tracking-wider">
          CONSCIOUSNESS TECHNOLOGY
        </p>
      </div>

      {/* Constellation */}
      <div className="relative z-10 flex items-center gap-6">
        {CONSTELLATION_STARS.map((star) => (
          <div
            key={star.name}
            className={cn(
              'flex flex-col items-center gap-2',
              'animate-pulse'
            )}
            style={{
              animationDelay: `${star.delay}s`,
              animationDuration: '3s'
            }}
          >
            <span className="text-2xl">{star.symbol}</span>
            <span className="text-[10px] text-gray-600 uppercase tracking-wider">
              {star.name}
            </span>
          </div>
        ))}
      </div>

      {/* Hint */}
      <p className="absolute bottom-8 text-gray-700 text-sm">
        Touch anywhere to wake
      </p>
    </div>
  )
}
